// =====================================================
// SUBMIT DEAL FORM TYPES (11-Step Master Form)
// =====================================================

export interface TOVScore {
  // Pilier 1 — Financier (0-30 pts)
  revenueStability: 0 | 1 | 2 | 3;
  ebitdaTrend: 0 | 1 | 2 | 3;
  grossMarginLevel: 0 | 1 | 2 | 3;
  debtLevel: 0 | 1 | 2 | 3;
  cashFlowPredictability: 0 | 1 | 2 | 3;
  sellerFinancingOffered: 0 | 1 | 2;

  // Pilier 2 — Capital Humain (0-25 pts)
  managementTeamStrength: 0 | 1 | 2 | 3 | 4 | 5;
  employeeRetention: 0 | 1 | 2 | 3;
  ownerDependency: 0 | 1 | 2 | 3;
  keyPersonRisk: 0 | 1 | 2 | 3;
  hrDocumentation: 0 | 1 | 2;

  // Pilier 3 — Marché & Clients (0-25 pts)
  customerDiversification: 0 | 1 | 2 | 3 | 4 | 5;
  marketPosition: 0 | 1 | 2 | 3;
  contractBacklog: 0 | 1 | 2 | 3;
  competitiveAdvantage: 0 | 1 | 2 | 3;
  marketGrowthTrend: 0 | 1 | 2;

  // Pilier 4 — Opérations & Gouvernance (0-20 pts)
  processDocumentation: 0 | 1 | 2 | 3;
  technologyModernity: 0 | 1 | 2 | 3;
  legalCompliance: 0 | 1 | 2 | 3;
  realEstateStability: 0 | 1 | 2;
  certifications: 0 | 1 | 2;

  // Calculated (auto)
  totalScore: number;
  financialScore: number;
  humanCapitalScore: number;
  marketScore: number;
  operationsScore: number;
}

export type DealCurrency = 'USD' | 'CAD' | 'EUR' | 'GBP';

export interface DealOwner {
  id: string;
  name: string;
  ownershipPercent: number;
  role: string;
  email: string;
  phone: string;
}

export type DistanceUnit = 'km' | 'miles';

export interface SubmitDealFormData {
  currentStep: number; // 1-11

  general: {
    legalName: string;
    tradeName: string;
    entityType: EntityType;
    ownershipCount: number;
    primaryOwnerPercent: number;
    owners: DealOwner[];
    title: string;
    description: string;
    reasonForSale: string;
    industry: string;
    naicsCode: string;
    locationCity: string;
    locationState: string;
    locationCountry: string;
    yearEstablished: number;
    previouslyListed: boolean;
    website: string;
    currency: DealCurrency;
    representedSellerId: string;
    representedSellerName: string;
    representedSellerEmail: string;
    brokerId: string;
  };

  operations: {
    fullTimeEmployees: number;
    partTimeEmployees: number;
    subcontractors: number;
    dailyOperationsDescription: string;
    processDocumentationLevel: 1 | 2 | 3 | 4 | 5;
    customerConcentration: number;
    keySuppliers: string;
    ownerHoursPerWeek: number;
    ownerType: OwnerType;
    ownerAnnualSalary: number;
    ownerCriticalTasks: string;
    ownerAbsenceImpact: string;
    languages: string;
    employeeTurnoverRate: number;
    unionAffiliated: boolean;
    unionDetails: string;
  };

  licensing: {
    specialLicensesRequired: boolean;
    licenseDetails: string;
    regulatoryRequirements: string;
    legalDisputes: string;
  };

  // AJOUTÉ — section technologie/outils, inspirée d'un exemple de CIM réel
  // pour une entreprise SaaS (Jinee Green Card via WebsiteClosers.com),
  // qui capture précisément ce type d'information (pile technologique,
  // outils tiers, transférabilité des abonnements). Regroupée avec
  // licensing plutôt qu'en étape séparée — les deux répondent à la même
  // question de fond : "qu'est-ce qui est requis/possédé pour opérer
  // légalement et techniquement cette entreprise ?"
  technology: {
    hasProprietaryTech: boolean;
    proprietaryTechDescription: string;
    techStackDescription: string;
    thirdPartyTools: string;
    toolsTransferable: boolean;
    requiredBuyerTechSkills: string;
    hostingProvider: string;
  };

  assets: {
    inventoryValue: number;
    ffeValue: number;
    accountsReceivable: number;
    otherCurrentAssets: number;
    patentsOrTrademarks: boolean;
    intellectualPropertyDetails: string;
    inventoryItems: InventoryItem[];
    assetListDocuments: UploadedFile[];
  };

  realEstate: {
    isIncluded: boolean;
    realEstateAskPrice: number;
    monthlyRent: number;
    leaseExpirationDate: string;
    properties: RealEstateProperty[];
  };

  financials: {
    askingPrice: number;
    includeRealEstateInPrice: boolean;
    totalAskingPriceWithRealEstate: number;
    revenueYear1: number;
    revenueYear2: number;
    revenueYear3: number;
    ebitdaYear1: number;
    ebitdaYear2: number;
    ebitdaYear3: number;
    cashFlowEquity: number;
    totalLiabilities: number;
    inheritedDebt: number;
    grossRevenue: number;
    ebitda: number;
    sde: number;
    revenueGrowth3yr: number;
  };

  addbacks: {
    ownerSalary: number;
    autoExpense: number;
    travelEntertainment: number;
    nonRecurringLegal: number;
    otherDiscretionary: number;
    addbackRationale: string;
  };

  projections: {
    revenueYear1: number;
    revenueYear2: number;
    revenueYear3: number;
    ebitdaYear1: number;
    ebitdaYear2: number;
    ebitdaYear3: number;
    revenueGrowthForecast: number;
    ebitdaGrowthForecast: number;
    growthOpportunities: string;
    sellerFinancingOffered: boolean;
    sellerFinancingDetails: string;
    sellerFinancingAmount: number;
    sellerFinancingPercentage: number;
    sellerFinancingInterestRate: number;
    sellerFinancingTermMonths: number;
    sellerFinancingDownPaymentPercent: number;
    sellerFinancingStandbyPeriodMonths: number;
    sellerFinancingCollateralType: string;
    sellerFinancingNotes: string;
  };

  culture: {
    coreValues: string[];
    coreValueDescriptions: string[];
    managementTeam: 'yes' | 'no';
    managementTeamExplanation: string;
    idealSuccessorDescription: string;
    biggestFearRegardingSale: string;
    postSaleConsultingDetails?: string;
    transitionDurationPreference: TransitionDuration;
    nonCompetePeriod: string;
    nonCompeteRadius: number;
    nonCompeteRadiusUnit: DistanceUnit;
    dealBreakersFromSeller: string;
    currentChallenges: string;
    growthGoals: string;
  };

  swot: {
    strengths: string;
    weaknesses: string;
    opportunities: string;
    threats: string;
  };

  documents: {
    confidentialityLevel: ConfidentialityLevel;
    lendersShareConsent: boolean;
    publicAnonymizationFlags: {
      hideLegalName: boolean;
      hideExactLocation: boolean;
      hideOwnerName: boolean;
    };
  };

  impact: {
    employeeCount: number;
    averageEmployeeTenureYears: number;
    hasRetirementPlan: boolean;
    workplaceAccidentsLast3yr: number;
    localSupplyChainPercent: number;
    hasEnvironmentalCertification: boolean;
    hasLitigationHistory: boolean;
    communityInvolvement: boolean;
  };

  tovScore: TOVScore;
}

export type EntityType =
  | 'llc'
  | 'cCorp'
  | 'sCorp'
  | 'partnership'
  | 'soleProprietorship'
  | 'other'
  | '';

export type OwnerType = 'active' | 'semiActive' | 'absentee';

export type ConsultingAvailability =
  | 'yes_full_time'
  | 'yes_part_time'
  | 'yes_on_demand'
  | 'negotiable'
  | 'yes'
  | 'no';

export type TransitionDuration = '30_days' | '60_days' | '90_days' | '6_months' | '12_months' | 'more_than_90_days';

export type ConfidentialityLevel = 'confidential' | 'public';

export type PropertyStatus = 'owned' | 'leased';

export interface RealEstateProperty {
  id: string;
  address: string;
  estimatedValue: number;
  status: PropertyStatus;
  monthlyRent: number;
  leaseExpirationDate: string;
  remainingLeaseMonths: number;
  renewalTerms: string;
  assignmentRight: boolean;
}

export interface InventoryItem {
  id: string;
  description: string;
  category: string;
  quantity: number;
  unitValue: number;
}

export interface UploadedFile {
  name: string;
  size: number;
  type: string;
  url: string;
  path?: string;
}

export interface UploadedFiles {
  documents: UploadedFile[];
  photos: UploadedFile[];
  logo: UploadedFile | null;
}

export interface SubmitDealStepProps {
  formData: SubmitDealFormData;
  updateFormData: <K extends keyof SubmitDealFormData>(
    section: K,
    updates: Partial<SubmitDealFormData[K]>
  ) => void;
  errors: Record<string, string>;
  clearError: (field: string) => void;
  t: import('@/constants/submit-deal.translations').SubmitDealTranslations;
}

// Initial form data factory
export const createInitialFormData = (): SubmitDealFormData => ({
  currentStep: 1,
  general: {
    legalName: '',
    tradeName: '',
    entityType: '',
    ownershipCount: 1,
    primaryOwnerPercent: 100,
    owners: [],
    title: '',
    description: '',
    reasonForSale: '',
    industry: '',
    naicsCode: '',
    locationCity: '',
    locationState: '',
    locationCountry: '',
    yearEstablished: new Date().getFullYear() - 5,
    previouslyListed: false,
    website: '',
    currency: 'CAD',
    representedSellerId: '',
    representedSellerName: '',
    representedSellerEmail: '',
    brokerId: '',
  },
  operations: {
    fullTimeEmployees: 0,
    partTimeEmployees: 0,
    subcontractors: 0,
    dailyOperationsDescription: '',
    processDocumentationLevel: 3,
    customerConcentration: 0,
    keySuppliers: '',
    ownerHoursPerWeek: 40,
    ownerType: 'active',
    ownerAnnualSalary: 0,
    ownerCriticalTasks: '',
    ownerAbsenceImpact: '',
    languages: '',
    employeeTurnoverRate: 0,
    unionAffiliated: false,
    unionDetails: '',
  },
  licensing: {
    specialLicensesRequired: false,
    licenseDetails: '',
    regulatoryRequirements: '',
    legalDisputes: '',
  },
  technology: {
    hasProprietaryTech: false,
    proprietaryTechDescription: '',
    techStackDescription: '',
    thirdPartyTools: '',
    toolsTransferable: false,
    requiredBuyerTechSkills: '',
    hostingProvider: '',
  },
  assets: {
    inventoryValue: 0,
    ffeValue: 0,
    accountsReceivable: 0,
    otherCurrentAssets: 0,
    patentsOrTrademarks: false,
    intellectualPropertyDetails: '',
    inventoryItems: [],
    assetListDocuments: [],
  },
  realEstate: {
    isIncluded: false,
    realEstateAskPrice: 0,
    monthlyRent: 0,
    leaseExpirationDate: '',
    properties: [],
  },
  financials: {
    askingPrice: '' as unknown as number,
    includeRealEstateInPrice: false,
    totalAskingPriceWithRealEstate: 0,
    revenueYear1: 0,
    revenueYear2: 0,
    revenueYear3: 0,
    ebitdaYear1: 0,
    ebitdaYear2: 0,
    ebitdaYear3: 0,
    cashFlowEquity: '' as unknown as number,
    totalLiabilities: 0,
    inheritedDebt: 0,
    grossRevenue: 0,
    ebitda: 0,
    sde: 0,
    revenueGrowth3yr: 0,
  },
  addbacks: {
    ownerSalary: 0,
    autoExpense: 0,
    travelEntertainment: 0,
    nonRecurringLegal: 0,
    otherDiscretionary: 0,
    addbackRationale: '',
  },
  projections: {
    revenueYear1: 0,
    revenueYear2: 0,
    revenueYear3: 0,
    ebitdaYear1: 0,
    ebitdaYear2: 0,
    ebitdaYear3: 0,
    revenueGrowthForecast: 0,
    ebitdaGrowthForecast: 0,
    growthOpportunities: '',
    sellerFinancingOffered: false,
    sellerFinancingDetails: '',
    sellerFinancingAmount: 0,
    sellerFinancingPercentage: 0,
    sellerFinancingInterestRate: 0,
    sellerFinancingTermMonths: 0,
    sellerFinancingDownPaymentPercent: 0,
    sellerFinancingStandbyPeriodMonths: 0,
    sellerFinancingCollateralType: '',
    sellerFinancingNotes: '',
  },
  culture: {
    coreValues: [''],
    coreValueDescriptions: [''],
    managementTeam: 'no',
    managementTeamExplanation: '',
    idealSuccessorDescription: '',
    biggestFearRegardingSale: '',
    postSaleConsultingDetails: '',
    transitionDurationPreference: '60_days',
    nonCompetePeriod: '',
    nonCompeteRadius: 0,
    nonCompeteRadiusUnit: 'km',
    dealBreakersFromSeller: '',
    currentChallenges: '',
    growthGoals: '',
  },
  swot: {
    strengths: '',
    weaknesses: '',
    opportunities: '',
    threats: '',
  },
  documents: {
    confidentialityLevel: 'confidential',
    // CORRIGÉ — précoché par défaut : ce n'est pas une véritable option
    // facultative dans le processus normal (voir StepDocuments.tsx, mis
    // en évidence visuellement pour la même raison). Le vendeur/broker
    // peut toujours le décocher s'il le souhaite réellement.
    lendersShareConsent: true,
    publicAnonymizationFlags: {
      hideLegalName: true,
      hideExactLocation: true,
      hideOwnerName: true,
    },
  },
  impact: {
    employeeCount: 1,
    averageEmployeeTenureYears: 0,
    hasRetirementPlan: false,
    workplaceAccidentsLast3yr: 0,
    localSupplyChainPercent: 0,
    hasEnvironmentalCertification: false,
    hasLitigationHistory: false,
    communityInvolvement: false,
  },
  tovScore: {
    revenueStability: 0,
    ebitdaTrend: 0,
    grossMarginLevel: 0,
    debtLevel: 0,
    cashFlowPredictability: 0,
    sellerFinancingOffered: 0,
    managementTeamStrength: 0,
    employeeRetention: 0,
    ownerDependency: 0,
    keyPersonRisk: 0,
    hrDocumentation: 0,
    customerDiversification: 0,
    marketPosition: 0,
    contractBacklog: 0,
    competitiveAdvantage: 0,
    marketGrowthTrend: 0,
    processDocumentation: 0,
    technologyModernity: 0,
    legalCompliance: 0,
    realEstateStability: 0,
    certifications: 0,
    totalScore: 0,
    financialScore: 0,
    humanCapitalScore: 0,
    marketScore: 0,
    operationsScore: 0,
  },
});